import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { MessageText } from "../../constants/messageText";
import { SnackBarMessage } from "../../components/snackBar/snackBar";
import { GetConfigByRole, GetUserRoles } from "../../constants/api-urlConstants";
import instance from "../../utils/axiosInstance";
import { apiBasePath } from "../../constants/env-url";

type BooleanField = {
  label: string;
  type: "BOOLEAN";
  value: boolean;
};

type RangePriceRow = {
  id?: number;
  from: number;
  to: number | "";
  price: number | "";
  order: number;
};

type RangePriceField = {
  label: string;
  type: "RANGE";
  value: RangePriceRow[];
};

type ConfigField = BooleanField | RangePriceField;

type ConfigModule = {
  title: string;
  description: string;
  fields: Record<string, ConfigField>;
};

type ConfigSchema = Record<string, ConfigModule>;

const ensureSpecialLastRangeRow = (rows: RangePriceRow[]) => {
  const sortedRows = [...rows]
    .sort((a, b) => a.order - b.order)
    .map((row, index) => ({
      ...row,
      order: index + 1,
    }));

  const hasRows = sortedRows.length > 0;
  const existingSpecialRow = hasRows ? sortedRows[sortedRows.length - 1] : undefined;
  const boundedRows = hasRows ? sortedRows.slice(0, -1) : [];
  const normalizedBoundedRows = boundedRows.map((row, index) => {
    if (index === 0) {
      return {
        ...row,
        from: 1,
      };
    }
    const previousTo = Number(boundedRows[index - 1]?.to);
    return {
      ...row,
      from: Number.isNaN(previousTo) ? 1 : Math.max(previousTo + 1, 1),
    };
  });

  const previousBoundedTo = normalizedBoundedRows.length
    ? Number(normalizedBoundedRows[normalizedBoundedRows.length - 1].to)
    : 0;
  const specialFrom = Number.isNaN(previousBoundedTo) ? 1 : Math.max(previousBoundedTo + 1, 1);

  const specialRow: RangePriceRow = {
    id: existingSpecialRow?.id,
    from: specialFrom,
    to: "",
    price: existingSpecialRow?.price ?? "",
    order: normalizedBoundedRows.length + 1,
  };

  return [...normalizedBoundedRows, specialRow].map((row, index) => ({
    ...row,
    order: index + 1,
  }));
};

const buildInitialValues = (schema: ConfigSchema) => {
  const values: any = {
    roleId: "",
  };
  Object.entries(schema).forEach(([moduleKey, moduleConfig]) => {
    values[moduleKey] = { fields: {} as any };
    Object.entries(moduleConfig.fields).forEach(([fieldKey, fieldConfig]) => {
      const fieldValue =
        fieldConfig.type === "RANGE"
          ? ensureSpecialLastRangeRow(fieldConfig.value || [])
          : fieldConfig.value;
      values[moduleKey].fields[fieldKey] = {
        value: fieldValue,
      };
    });
  });
  return values;
};

const getNextOrderValue = (rows: RangePriceRow[]) => {
  if (!rows.length) {
    return 1;
  }
  return Math.max(...rows.map((item) => item.order || 0)) + 1;
};

const normalizeRangeFields = (configs: any) => {
  const normalizedConfigs = { ...configs };

  Object.entries(normalizedConfigs).forEach(([moduleKey, moduleConfig]: [string, any]) => {
    const fields = moduleConfig?.fields || {};
    const normalizedFields = { ...fields };

    Object.entries(normalizedFields).forEach(([fieldKey, fieldValue]: [string, any]) => {
      const rows = fieldValue?.value;
      if (!Array.isArray(rows)) {
        return;
      }

      const updatedRows = rows.map((row: RangePriceRow, rowIndex: number) => {
        if (rowIndex !== 0) {
          return row;
        }
        return {
          ...row,
          from: 1,
        };
      });

      normalizedFields[fieldKey] = {
        ...fieldValue,
        value: ensureSpecialLastRangeRow(updatedRows),
      };
    });

    normalizedConfigs[moduleKey] = {
      ...moduleConfig,
      fields: normalizedFields,
    };
  });

  return normalizedConfigs;
};

const recomputeFromValues = (rows: RangePriceRow[]) => {
  return ensureSpecialLastRangeRow(rows);
};

const getConfigValidationSchema = (schema: ConfigSchema) => {
  const rowSchema = Yup.object().shape({
    from: Yup.number()
      .required(MessageText.VALIDATION_FROM_REQUIRED)
      .min(1, MessageText.VALIDATION_FROM_MIN),
    to: Yup.number()
      .typeError(MessageText.VALIDATION_TO_REQUIRED)
      .nullable()
      .min(1, MessageText.VALIDATION_TO_MIN)
      .test("to-greater-than-from", MessageText.VALIDATION_TO_GREATER_THAN_FROM, function (value) {
        if (value === undefined || value === null) {
          return true;
        }
        const from = Number(this.parent?.from);
        if (Number.isNaN(from)) {
          return true;
        }
        return Number(value) > from;
      }),
    price: Yup.number()
      .typeError(MessageText.VALIDATION_PRICE_REQUIRED)
      .required(MessageText.VALIDATION_PRICE_REQUIRED)
      .min(1, MessageText.VALIDATION_PRICE_MIN),
    order: Yup.number().required(),
    id: Yup.number().notRequired(),
  });
  const bundlePriceRowSchema = Yup.object().shape({
    from: Yup.number()
      .required(MessageText.VALIDATION_FROM_REQUIRED)
      .min(1, MessageText.VALIDATION_FROM_MIN),
    to: Yup.number()
      .typeError(MessageText.VALIDATION_TO_REQUIRED)
      .nullable()
      .min(1, MessageText.VALIDATION_TO_MIN)
      .test("to-greater-than-from", MessageText.VALIDATION_TO_GREATER_THAN_FROM, function (value) {
        if (value === undefined || value === null) {
          return true;
        }
        const from = Number(this.parent?.from);
        if (Number.isNaN(from)) {
          return true;
        }
        return Number(value) > from;
      }),
    price: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
          return undefined;
        }
        return value;
      })
      .typeError(MessageText.VALIDATION_PRICE_REQUIRED)
      .notRequired()
      .min(1, MessageText.VALIDATION_PRICE_MIN),
    order: Yup.number().required(),
    id: Yup.number().notRequired(),
  });

  const moduleSchemaMap: Record<string, Yup.AnySchema> = {};

  Object.entries(schema || {}).forEach(([moduleKey, moduleConfig]) => {
    const fieldSchemaMap: Record<string, Yup.AnySchema> = {};

    Object.entries(moduleConfig.fields || {}).forEach(([fieldKey, fieldConfig]) => {
      if (fieldConfig.type === "RANGE") {
        const rangeRowSchema = fieldKey === "bundlePrice" ? bundlePriceRowSchema : rowSchema;
        fieldSchemaMap[fieldKey] = Yup.object().shape({
          value: Yup.array().of(rangeRowSchema),
        });
      } else {
        fieldSchemaMap[fieldKey] = Yup.object().shape({
          value: Yup.boolean().required(),
        });
      }
    });

    moduleSchemaMap[moduleKey] = Yup.object()
      .shape({
        fields: Yup.object().shape(fieldSchemaMap),
      })
      .test(
        "bundle-price-required-when-visible",
        MessageText.VALIDATION_PRICE_REQUIRED,
        function (moduleValue) {
          const fields = (moduleValue?.fields || {}) as Record<string, any>;
          const shouldValidateBundlePrice =
            Boolean(fields?.showBundlePrice?.value) && Boolean(fields?.showTab?.value);

          if (!shouldValidateBundlePrice) {
            return true;
          }

          const bundlePriceRows = fields?.bundlePrice?.value;
          if (!Array.isArray(bundlePriceRows)) {
            return true;
          }

          const missingPriceIndex = bundlePriceRows.findIndex(
            (row: RangePriceRow) => row?.price === undefined || row?.price === null || row?.price === ""
          );

          if (missingPriceIndex === -1) {
            return true;
          }

          return this.createError({
            path: `${this.path}.fields.bundlePrice.value[${missingPriceIndex}].price`,
            message: MessageText.VALIDATION_PRICE_REQUIRED,
          });
        }
      );
  });

  return Yup.object().shape({
    roleId: Yup.string().notRequired(),
    ...moduleSchemaMap,
  });
};

const Config = () => {
  const [roles, setRoles] = React.useState<any[]>([]);
  const [selectedRoleId, setSelectedRoleId] = React.useState<string>("");
  const [configByRole, setConfigByRole] = React.useState<ConfigSchema>({});
  const [msgConfig, setMsgConfig] = React.useState({
    type: "",
    message: "",
    snackFlag: false,
  });

  React.useEffect(() => {
    getRoles();
  }, []);

  const getRoles = () => {
    const USER_API_BASE_URL = `${GetUserRoles}`;
    instance.get(USER_API_BASE_URL).then((response) => {
      const res = response.data;
      setRoles(res.data);
    })
      .catch(() => { });
  }

  const onChangeRole = (roleId: string) => {
    setSelectedRoleId(roleId);
    getConfigByRole(roleId);
  }

  const getConfigByRole = (roleId: string) => {
    const USER_API_BASE_URL = `${apiBasePath}${GetConfigByRole}/${roleId}`;
    instance.get(USER_API_BASE_URL).then((response) => {
      const res = response.data;
      setConfigByRole(res.data.configs);
    })
      .catch(() => { });
  }

  const onSubmitForm = (values: any) => {
    delete values.roleId;
    const normalizedValues = normalizeRangeFields(values);
    const payload = {
      roleId: selectedRoleId,
      configs: normalizedValues
    }
    const USER_API_BASE_URL = `${apiBasePath}${GetConfigByRole}/${selectedRoleId}`;
    instance.post(USER_API_BASE_URL, payload).then((response) => {
      const res = response.data;
      if (res.success) {
        setMsgConfig({
          type: "success",
          message: MessageText.CONFIGURATION_SAVED_SUCCESSFULLY,
          snackFlag: true,
        });
      } else {
        setMsgConfig({
          type: "error",
          message: res.message_code, // MessageText.CONFIGURATION_SAVED_FAILED,
          snackFlag: true,
        });
      }
    })
      .catch(() => {
        setMsgConfig({
          type: "error",
          message: MessageText.CONFIGURATION_SAVED_FAILED,
          snackFlag: true,
        });
      });
  }


  return (
    <Box sx={{ width: '100%', minHeight: '90vh', display: 'flex', alignItems: 'center', flexDirection: 'column', px: '24px', py: '30px', background: '#f5f5f5' }}>
      <Box sx={{ width: '70vw' }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure module visibility and range-based pricing.
        </Typography>

        <Formik
          initialValues={buildInitialValues(configByRole)}
          validationSchema={getConfigValidationSchema(configByRole)}
          enableReinitialize
          validateOnMount={false}
          onSubmit={(values) => {
            onSubmitForm(values);
          }}
        >
          {({ values, errors, touched, handleChange, setFieldValue, resetForm }) => (
            <Form noValidate>
              <Stack spacing={2.5}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Role
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                      <InputLabel id="config-role-label">Role</InputLabel>
                      <Select
                        labelId="config-role-label"
                        id="config-role"
                        name="roleId"
                        value={selectedRoleId}
                        label="Role"
                        onChange={e => {
                          onChangeRole(e.target.value);
                        }}
                      >
                        {roles.map((role: any) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role?.name?.value || role?.name || role?.key || role?.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>

                {Object.entries(configByRole).map(([moduleKey, moduleConfig]) => (
                  <Card key={moduleKey} variant="outlined">
                    <CardContent>
                      <Typography variant="h6">{moduleConfig.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {moduleConfig.description}
                      </Typography>

                      <Stack spacing={2}>
                        {Object.entries(moduleConfig.fields).map(([fieldKey, fieldConfig]) => {
                          const fieldName = `${moduleKey}.fields.${fieldKey}.value`;
                          const isBundlePrice = fieldKey === "bundlePrice";

                          if ((fieldConfig.type === "BOOLEAN" && fieldKey !== 'showBundlePrice') || (fieldConfig.type === "BOOLEAN" && fieldKey === 'showBundlePrice' && Boolean(values?.[moduleKey]?.fields?.['showTab']?.value))) {
                            return (
                              <Box
                                key={`${moduleKey}-${fieldKey}`}
                                sx={{
                                  width: '100%',
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between"
                                }}
                              >
                                <Typography variant="body1">{fieldConfig.label}</Typography>
                                <Switch
                                  checked={Boolean(values?.[moduleKey]?.fields?.[fieldKey]?.value)}
                                  onChange={(event) => {
                                    setFieldValue(fieldName, event.target.checked);
                                  }}
                                />
                              </Box>
                            );
                          }

                          if ((fieldConfig.type === "RANGE" && fieldKey !== 'bundlePrice') || (fieldConfig.type === "RANGE" && fieldKey === 'bundlePrice' && values?.[moduleKey]?.fields?.['showBundlePrice']?.value && Boolean(values?.[moduleKey]?.fields?.['showTab']?.value))) {
                            const rows: RangePriceRow[] = ensureSpecialLastRangeRow(
                              values?.[moduleKey]?.fields?.[fieldKey]?.value || [],
                            );
                            const sortedRows = [...rows].sort((a, b) => a.order - b.order);
                            const lastRow = sortedRows.length ? sortedRows[sortedRows.length - 1] : null;
                            const lastBoundedRow =
                              sortedRows.length > 1 ? sortedRows[sortedRows.length - 2] : null;
                            const isAddRangeDisabled = Boolean(
                              lastRow &&
                              ((lastBoundedRow && (lastBoundedRow.to === "" || Number(lastBoundedRow.to) < 1)) ||
                                (!lastBoundedRow && lastRow.to !== "" && Number(lastRow.to) < 1)),
                            );
                            return (
                              <Box key={`${moduleKey}-${fieldKey}`}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  {fieldConfig.label}
                                </Typography>
                                <Stack spacing={1.5}>
                                  {sortedRows.map((_, index) => {
                                    const isSpecialLastRow = index === sortedRows.length - 1;
                                    return (
                                      <Box
                                        key={`${moduleKey}-${fieldKey}-${index}`}
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "1fr 1fr 1fr auto auto",
                                          gap: 1,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <TextField
                                          label="From"
                                          size="small"
                                          type="number"
                                          name={`${fieldName}.${index}.from`}
                                          value={index === 0 ? 1 : (sortedRows[index]?.from ?? "")}
                                          disabled
                                          inputProps={{ min: 1 }}
                                          onChange={handleChange}
                                        />
                                        {!isSpecialLastRow ? (
                                          <TextField
                                            label="To"
                                            size="small"
                                            type="number"
                                            name={`${fieldName}.${index}.to`}
                                            value={sortedRows[index]?.to ?? ""}
                                            error={Boolean(
                                              getIn(
                                                touched,
                                                `${fieldName}.${index}.to`,
                                              ) && getIn(errors, `${fieldName}.${index}.to`),
                                            )}
                                            helperText={
                                              (getIn(touched, `${fieldName}.${index}.to`) &&
                                                getIn(errors, `${fieldName}.${index}.to`)) ||
                                              ""
                                            }
                                            inputProps={{ min: 1 }}
                                            onChange={(event) => {
                                              const inputValue = event.target.value;
                                              const updatedRows = [...sortedRows];
                                              const currentRow = updatedRows[index];
                                              const isEmpty = inputValue === "";
                                              const parsedValue = Number(inputValue);
                                              const normalizedTo = isEmpty
                                                ? ""
                                                : (Number.isNaN(parsedValue) ? "" : Math.max(parsedValue, 1));

                                              updatedRows[index] = {
                                                id: currentRow?.id,
                                                from: index === 0 ? 1 : Number(currentRow?.from ?? 1),
                                                to: normalizedTo,
                                                price: currentRow?.price ?? "",
                                                order: Number(currentRow?.order ?? index + 1),
                                              };

                                              setFieldValue(fieldName, recomputeFromValues(updatedRows));
                                            }}
                                          />
                                        ) : <Box sx={{ border: '1px solid', borderColor: '#c4c4c4', borderRadius: '5px', p: '8.5px 14px', color: '#c4c4c4'}}>and above</Box>}
                                        <TextField
                                          label="Price"
                                          size="small"
                                          type="number"
                                          name={`${fieldName}.${index}.price`}
                                          value={sortedRows[index]?.price ?? ""}
                                          error={Boolean(
                                            getIn(
                                              touched,
                                              `${fieldName}.${index}.price`,
                                            ) && getIn(errors, `${fieldName}.${index}.price`),
                                          )}
                                          helperText={
                                            (getIn(touched, `${fieldName}.${index}.price`) &&
                                              getIn(errors, `${fieldName}.${index}.price`)) ||
                                            ""
                                          }
                                          inputProps={{ min: 1 }}
                                          onChange={(event) => {
                                            const inputValue = event.target.value;
                                            if (inputValue === "") {
                                              setFieldValue(`${fieldName}.${index}.price`, "");
                                              return;
                                            }
                                            const parsedValue = Number(inputValue);
                                            if (Number.isNaN(parsedValue)) {
                                              setFieldValue(`${fieldName}.${index}.price`, "");
                                              return;
                                            }
                                            setFieldValue(`${fieldName}.${index}.price`, Math.max(parsedValue, 1));
                                          }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ p: '7px' }}>
                                          #{sortedRows[index]?.order}
                                        </Typography>
                                        <IconButton
                                          color="error"
                                          size="small"
                                          disabled={sortedRows.length === 1 || isSpecialLastRow}
                                          onClick={() => {
                                            const updatedRows = sortedRows
                                              .filter((__, rowIndex) => rowIndex !== index)
                                              .map((row, rowIndex) => ({
                                                ...row,
                                                order: rowIndex + 1,
                                              }));
                                            setFieldValue(fieldName, recomputeFromValues(updatedRows));
                                          }}
                                        >
                                          <RemoveCircleOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    );
                                  })}
                                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                    <Button
                                      size="small"
                                      type="button"
                                      disabled={isAddRangeDisabled || sortedRows.length === 0}
                                      startIcon={<AddCircleOutlineIcon />}
                                      onClick={() => {
                                        const nextOrder = getNextOrderValue(sortedRows);
                                        const updatedRows = [...sortedRows];
                                        const specialLastRow = updatedRows[updatedRows.length - 1];
                                        const defaultFrom = specialLastRow
                                          ? Number(specialLastRow.from || 1)
                                          : 1;
                                        const fromValue = Math.max(defaultFrom, 1);
                                        const newRow: RangePriceRow = {
                                          from: fromValue,
                                          to: "",
                                          price: "",
                                          order: nextOrder,
                                        };
                                        if (specialLastRow) {
                                          updatedRows[updatedRows.length - 1] = {
                                            ...specialLastRow,
                                            to: fromValue,
                                          };
                                        }
                                        setFieldValue(fieldName, recomputeFromValues([...updatedRows, newRow]));
                                      }}
                                    >
                                      Add Range
                                    </Button>
                                  </Box>
                                </Stack>
                              </Box>
                            );
                          }

                          return null;
                        })}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              {selectedRoleId && <>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                  <Button
                    type="button"
                    onClick={() => {
                      resetForm();
                    }}
                  >
                    Reset
                  </Button>
                  <Button variant="contained" type="submit">
                    Save Configuration
                  </Button>
                </Box></>}
            </Form>
          )}
        </Formik>

        {msgConfig.snackFlag && <SnackBarMessage snacBarValue={msgConfig} />}
      </Box>
    </Box>
  );
};

export default Config;